import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import CircularProgress from "@mui/material/CircularProgress";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";

import axios from "axios";
import ConstList from "../utils/ConstList";

import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";
import { designTokens } from "../theme";

type Props = {
  Data: any;
  DeleteOpen: boolean;
  SetDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ReViewPage: React.Dispatch<React.SetStateAction<boolean>>;

  // 親画面の一覧から即時に対象行を除外するための通知
  onDeleted?: (companyId: number) => void;
};

type ConfirmStopDialogProps = {
  open: boolean;
  companyName: string;
  reason: string;
  confirmCompanyName: string;
  isSaving: boolean;
  canConfirm: boolean;
  onChangeConfirmCompanyName: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
};

const dangerColors = {
  bg: designTokens.surface.page,
  panel: designTokens.surface.paper,
  panelLight: designTokens.surface.subtle,
  danger: designTokens.state.error,
  dangerStrong: "#B42318",
  dangerDeep: "#7A271A",
  dangerSoft: "rgba(220, 90, 82, 0.08)",
  warning: designTokens.state.warning,
  text: designTokens.text.primary,
  textSoft: designTokens.text.secondary,
  textMuted: "#64748B",
  border: designTokens.border.default,
  borderDanger: "rgba(180, 35, 24, 0.30)",
};

const pickCompanyId = (data: any): number => {
  const raw =
    data?.Company_id ??
    data?.company_id ??
    data?.Customer_id ??
    data?.customer_id ??
    data?.CompanyId ??
    data?.companyId ??
    data?.id ??
    0;

  const id = Number(raw);

  return Number.isFinite(id) ? id : 0;
};

const isAlreadyStoppedMessage = (message: unknown): boolean => {
  const text = String(message ?? "");
  return (
    text.includes("既に使用停止") ||
    text.includes("使用停止中") ||
    text.includes("対象の顧客が見つからない")
  );
};

const ConfirmStopDialog = ({
  open,
  companyName,
  reason,
  confirmCompanyName,
  isSaving,
  canConfirm,
  onChangeConfirmCompanyName,
  onClose,
  onConfirm,
}: ConfirmStopDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${dangerColors.borderDanger}`,
          backgroundColor: dangerColors.panel,
          color: dangerColors.text,
        },
      }}
    >
      <DialogTitle
        sx={{
          background:
            "linear-gradient(135deg, #B42318 0%, #912018 55%, #7A271A 100%)",
          color: "common.white",
          borderBottom: `1px solid ${dangerColors.borderDanger}`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <WarningAmberRoundedIcon />
          <Box>
            <Typography fontWeight={900}>最終確認</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              この操作は通常の顧客一覧から企業を非表示にします
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: dangerColors.border }}>
        <Stack spacing={2.5}>
          <Alert
            severity="error"
            icon={<ErrorOutlineRoundedIcon />}
            sx={{
              borderRadius: 2,
              backgroundColor: dangerColors.dangerSoft,
              color: dangerColors.dangerDeep,
              border: `1px solid ${dangerColors.borderDanger}`,
              fontWeight: 700,
              "& .MuiAlert-icon": {
                color: dangerColors.dangerStrong,
              },
            }}
          >
            本当にこの企業を使用停止にしてよろしいですか？
          </Alert>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: dangerColors.panelLight,
              border: `1px solid ${dangerColors.borderDanger}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: dangerColors.textMuted, fontWeight: 700 }}
            >
              使用停止対象
            </Typography>

            <Typography variant="h6" fontWeight={900} sx={{ mt: 0.5 }}>
              {companyName || "-"}
            </Typography>
          </Paper>

          <Box>
            <Typography
              fontWeight={800}
              sx={{ mb: 1, color: dangerColors.text }}
            >
              使用停止後の扱い
            </Typography>

            <Stack spacing={1}>
              <ImpactItem text="通常の顧客一覧には表示されなくなります。" />
              <ImpactItem text="使用停止中顧客ページに移動します。" />
              <ImpactItem text="営業履歴・取引履歴は保持されます。" />
              <ImpactItem text="後から取引再開することは可能です。" />
            </Stack>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: dangerColors.panel,
              border: `1px solid ${dangerColors.border}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: dangerColors.textMuted, fontWeight: 700 }}
            >
              使用停止理由
            </Typography>

            <Typography
              sx={{
                mt: 0.75,
                whiteSpace: "pre-wrap",
                color: dangerColors.textSoft,
                lineHeight: 1.8,
              }}
            >
              {reason}
            </Typography>
          </Paper>

          <Box>
            <Typography
              fontWeight={800}
              sx={{ mb: 1, color: dangerColors.text }}
            >
              誤操作防止
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: dangerColors.textSoft, mb: 1 }}
            >
              停止する場合は、確認のため企業名を入力してください。
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="企業名を入力"
              value={confirmCompanyName}
              onChange={(event) =>
                onChangeConfirmCompanyName(event.target.value)
              }
              placeholder={companyName}
              disabled={isSaving}
              error={confirmCompanyName !== "" && !canConfirm}
              helperText={
                confirmCompanyName !== "" && !canConfirm
                  ? "企業名が一致していません。"
                  : `「${companyName}」と入力してください。`
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: dangerColors.panel,
                  color: dangerColors.text,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: designTokens.border.strong,
                },
                "& .MuiInputLabel-root": {
                  color: dangerColors.textSoft,
                },
                "& .MuiFormHelperText-root": {
                  color:
                    confirmCompanyName !== "" && !canConfirm
                      ? dangerColors.dangerStrong
                      : dangerColors.textSoft,
                  fontWeight:
                    confirmCompanyName !== "" && !canConfirm ? 700 : 500,
                },
              }}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={isSaving}
          variant="outlined"
          sx={{
            color: dangerColors.textSoft,
            borderColor: designTokens.border.strong,
            "&:hover": {
              color: dangerColors.text,
              borderColor: dangerColors.textMuted,
              backgroundColor: dangerColors.panelLight,
            },
          }}
        >
          やめる
        </Button>

        <Button
          onClick={onConfirm}
          disabled={isSaving || !canConfirm}
          sx={{
            backgroundColor: dangerColors.dangerStrong,
            color: "common.white",
            fontWeight: 900,
            "&:hover": {
              backgroundColor: dangerColors.dangerDeep,
            },
            "&.Mui-disabled": {
              backgroundColor: "#E7B8B4",
              color: "#6B2A25",
            },
          }}
        >
          {isSaving ? (
            <CircularProgress size={22} sx={{ color: "common.white" }} />
          ) : (
            "使用停止を確定する"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ImpactItem = ({ text }: { text: string }) => {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: dangerColors.danger,
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" sx={{ color: dangerColors.textSoft }}>
        {text}
      </Typography>
    </Stack>
  );
};

const ProsCustomerDelete = ({
  Data,
  DeleteOpen,
  SetDeleteOpen,
  ReViewPage,
  onDeleted,
}: Props) => {
  const loginUserData = useRecoilValue(LoginUserAtom);

  const [loading, setLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [deleteReason, setDeleteReason] = useState("");
  const [deleteReasonError, setDeleteReasonError] = useState("");
  const [confirmCompanyName, setConfirmCompanyName] = useState("");

  const companyName = Data?.Company_name ?? "";

  const getLoginUserDisplayName = () => {
    const candidates = [
      (loginUserData as any).userName,
      (loginUserData as any).UserName,
      (loginUserData as any).name,
      (loginUserData as any).displayName,
      (loginUserData as any).email,
      (loginUserData as any).userId,
      (loginUserData as any).UserId,
    ];

    const found = candidates
      .map((value) => String(value ?? "").trim())
      .find((value) => value !== "");

    return found ?? "unknown";
  };

  const canConfirm = useMemo(() => {
    return confirmCompanyName.trim() === companyName.trim();
  }, [confirmCompanyName, companyName]);

  useEffect(() => {
    setLoading(true);

    if (DeleteOpen) {
      setDeleteReason("");
      setDeleteReasonError("");
      setConfirmCompanyName("");
      setConfirmDialogOpen(false);
      setIsSaving(false);

      setTimeout(() => setLoading(false), 250);
    }
  }, [DeleteOpen]);

  const handleDeleteClick = () => {
    const reason = deleteReason.trim();

    if (!reason) {
      setDeleteReasonError("使用停止理由を入力してください。");
      return;
    }

    if (reason.length < 3) {
      setDeleteReasonError("使用停止理由は3文字以上で入力してください。");
      return;
    }

    setDeleteReasonError("");
    setConfirmCompanyName("");
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (isSaving) return;

    const reason = deleteReason.trim();

    if (!reason) {
      setDeleteReasonError("使用停止理由を入力してください。");
      setConfirmDialogOpen(false);
      return;
    }

    if (!canConfirm) {
      return;
    }

    setIsSaving(true);

    const companyId = pickCompanyId(Data);

    if (companyId <= 0) {
      alert("会社IDを取得できないため、使用停止できません。");
      setIsSaving(false);
      return;
    }

    const loginUserName = getLoginUserDisplayName();

    const SaveArray = {
      // PHP側の受け取り名の揺れに備えて両方送る
      Company_id: companyId,
      company_id: companyId,
      CompanyId: companyId,
      companyId: companyId,

      Company_name: Data?.Company_name ?? "",
      company_name: Data?.Company_name ?? "",

      // 使用停止者として保存する表示名
      deleted_by: loginUserName,
      Deleted_by: loginUserName,

      delete_reason: reason,
      Delete_reason: reason,
      deleteReason: reason,
    };

    try {
      const accessToken =
        (loginUserData as any).access_token ??
        (loginUserData as any).accessToken ??
        "";
      const algorithm = (loginUserData as any).algorithm ?? "";

      const postData = {
        flag: "DeleteProsCustomerInfo",
        access_token: accessToken,
        // APIの旧実装・現行実装のどちらでも認証できるよう両形式を送る。
        accessToken,
        algorithm,

        UserId: loginUserData.userId || "unknown",
        UserName: loginUserName,
        userName: loginUserName,

        SaveData: SaveArray,
      };

      const response = await axios.post(
        ConstList.getApiUrl() + "EditProsCustomers.php",
        postData,
        ConstList.AXIOS_CONFIG,
      );

      console.log("使用停止API結果", response.data);

      if (response.data?.message === "Auth認証失敗") {
        alert("エラー：Auth0認証失敗");
        setIsSaving(false);
        return;
      }

      if (response.data?.message === "メンテナンス中") {
        window.location.href = "./maintenance";
        return;
      }

      const isSuccess =
        response.data?.status === 1 ||
        response.data?.status === "1" ||
        response.data?.data === true ||
        response.data?.data?.status === 1 ||
        response.data?.data?.status === "1" ||
        response.data?.result === true;

      // APIから「既に使用停止中」または「見つからない」と返ってきた場合も、
      // 現在の通常一覧には残すべきではないため、画面上は成功扱いで除外する。
      const shouldRemoveFromActiveList =
        isSuccess || isAlreadyStoppedMessage(response.data?.message);

      if (shouldRemoveFromActiveList) {
        // 親画面の現在表示から即時除外
        onDeleted?.(companyId);

        // 親画面へ再取得依頼
        ReViewPage(true);

        setOpenSnackbar(true);

        setTimeout(() => {
          setOpenSnackbar(false);
          setConfirmDialogOpen(false);
          SetDeleteOpen(false);
          setIsSaving(false);
        }, 700);

        return;
      }

      alert(response.data?.message ?? "使用停止に失敗しました。");
      setIsSaving(false);
    } catch (error) {
      console.error("使用停止処理でエラーが発生しました：", error);
      alert("使用停止処理でエラーが発生しました。");
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;

    setDeleteReason("");
    setDeleteReasonError("");
    setConfirmCompanyName("");
    setConfirmDialogOpen(false);
    SetDeleteOpen(false);
  };

  return (
    <>
      <Dialog
        open={DeleteOpen}
        maxWidth="sm"
        fullWidth
        onClose={isSaving ? undefined : handleCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: dangerColors.panel,
            border: `1px solid ${dangerColors.borderDanger}`,
            boxShadow: "0 24px 64px rgba(15,23,42,0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background:
              "linear-gradient(135deg, #B42318 0%, #912018 55%, #7A271A 100%)",
            color: "common.white",
            borderBottom: `1px solid ${dangerColors.borderDanger}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <WarningAmberRoundedIcon />
            <Box>
              <Typography fontWeight={900}>顧客の使用停止</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                通常の顧客一覧から非表示にする操作です
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            backgroundColor: dangerColors.panel,
            borderColor: dangerColors.border,
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 180,
              }}
            >
              <CircularProgress />
              <Typography sx={{ ml: 2, color: dangerColors.textSoft }}>
                読み込み中...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.5}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: dangerColors.panelLight,
                  border: `1px solid ${dangerColors.border}`,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <BusinessOutlinedIcon sx={{ color: dangerColors.danger }} />

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: dangerColors.textMuted, fontWeight: 700 }}
                    >
                      使用停止対象企業
                    </Typography>

                    <Typography
                      variant="h6"
                      fontWeight={900}
                      sx={{
                        color: dangerColors.text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={companyName}
                    >
                      {companyName || "-"}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <TextField
                label="使用停止理由"
                value={deleteReason}
                onChange={(event) => {
                  setDeleteReason(event.target.value);

                  if (deleteReasonError) {
                    setDeleteReasonError("");
                  }
                }}
                fullWidth
                multiline
                minRows={4}
                required
                error={Boolean(deleteReasonError)}
                helperText={
                  deleteReasonError ||
                  "例：契約終了、重複登録、廃業、取引終了、誤登録など"
                }
                placeholder="使用停止にする理由を入力してください"
                disabled={isSaving}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: dangerColors.panel,
                    color: dangerColors.text,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: designTokens.border.strong,
                  },
                  "& .MuiInputLabel-root": {
                    color: dangerColors.textSoft,
                  },
                  "& .MuiFormHelperText-root": {
                    color: deleteReasonError
                      ? dangerColors.dangerStrong
                      : dangerColors.textSoft,
                    fontWeight: deleteReasonError ? 700 : 500,
                  },
                  "& textarea::placeholder": {
                    color: dangerColors.textMuted,
                    opacity: 1,
                  },
                }}
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            backgroundColor: dangerColors.panel,
            borderTop: `1px solid ${dangerColors.border}`,
          }}
        >
          <Button
            onClick={handleCancel}
            disabled={isSaving}
            variant="outlined"
            sx={{
              color: dangerColors.textSoft,
              borderColor: designTokens.border.strong,
              "&:hover": {
                color: dangerColors.text,
                borderColor: dangerColors.textMuted,
                backgroundColor: dangerColors.panelLight,
              },
            }}
          >
            キャンセル
          </Button>

          <Button
            onClick={handleDeleteClick}
            disabled={isSaving || loading}
            startIcon={<RestartAltOutlinedIcon />}
            sx={{
              backgroundColor: dangerColors.dangerStrong,
              color: "common.white",
              fontWeight: 900,
              "&:hover": {
                backgroundColor: dangerColors.dangerDeep,
              },
              "&.Mui-disabled": {
                backgroundColor: "#E7B8B4",
                color: "#6B2A25",
              },
            }}
          >
            {isSaving ? (
              <CircularProgress size={24} sx={{ color: "common.white" }} />
            ) : (
              "使用停止へ進む"
            )}
          </Button>
        </DialogActions>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={1000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="success">企業を使用停止にしました。</Alert>
        </Snackbar>
      </Dialog>

      <ConfirmStopDialog
        open={confirmDialogOpen}
        companyName={companyName}
        reason={deleteReason.trim()}
        confirmCompanyName={confirmCompanyName}
        isSaving={isSaving}
        canConfirm={canConfirm}
        onChangeConfirmCompanyName={setConfirmCompanyName}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default ProsCustomerDelete;
